import { useState, useEffect, useCallback, useMemo } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { sb } from '../lib/supabase';
import { useSupabaseAuth } from '../context/AuthContext';
import useWorkspaceAccess from '../hooks/useWorkspaceAccess';
import Badge from '../components/Badge';
import Loader from '../components/Loader';

const STATUS_META = {
  proposed: { label: 'Proposed', variant: 'am' },
  approved: { label: 'Approved', variant: 'gr' },
  filled: { label: 'Filled', variant: 'gr' },
  vacant: { label: 'Vacant', variant: 'rd' },
};

const DEFAULT_PHASES = ['Discover', 'Design', 'Build', 'Test', 'Deploy'];

const PROPOSAL_TEMPLATES = [
  { role_label: 'Programme Manager', category: 'Programme leadership', phases: DEFAULT_PHASES, estimated_days: 120 },
  { role_label: 'Solution Architect', category: 'Solution', phases: ['Discover', 'Design', 'Build'], estimated_days: 80 },
  { role_label: 'Business Analyst', category: 'Functional', phases: ['Discover', 'Design'], estimated_days: 60 },
  { role_label: 'Functional Consultant', category: 'Functional', phases: ['Design', 'Build', 'Test'], estimated_days: 90 },
  { role_label: 'Integration Architect', category: 'Solution', phases: ['Design', 'Build'], estimated_days: 45 },
  { role_label: 'Data Migration Lead', category: 'Data', phases: ['Design', 'Build', 'Test'], estimated_days: 55 },
  { role_label: 'Testing Lead', category: 'Testing', phases: ['Build', 'Test'], estimated_days: 40 },
  { role_label: 'Change Manager', category: 'Change', phases: ['Build', 'Test', 'Deploy'], estimated_days: 35 },
];

function assigneeName(row) {
  return row.assignee?.full_name || null;
}

export default function RoleProposal() {
  const { project, programme, stages } = useOutletContext();
  const { profile } = useSupabaseAuth();
  const { canWrite } = useWorkspaceAccess();
  const navigate = useNavigate();

  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [phaseFilter, setPhaseFilter] = useState('all');
  const [proposing, setProposing] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [toast, setToast] = useState(null);

  const orgId = programme?.organisation_id;

  const phases = useMemo(() => {
    if (stages?.length) return stages.map((s) => s.name);
    return DEFAULT_PHASES;
  }, [stages]);

  const load = useCallback(async () => {
    if (!project?.id) return;
    setLoading(true);
    try {
      const { data } = await sb
        .from('project_role_proposals')
        .select('*, assignee:profiles!project_role_proposals_assignee_id_fkey(full_name)')
        .eq('project_id', project.id)
        .order('order_index', { ascending: true });
      setRoles(data || []);
    } catch (e) {
      console.error('[RoleProposal] load failed', e);
    } finally {
      setLoading(false);
    }
  }, [project?.id]);

  useEffect(() => { load(); }, [load]);

  const summary = useMemo(() => ({
    total: roles.length,
    proposed: roles.filter((r) => r.status === 'proposed').length,
    vacant: roles.filter((r) => r.status === 'vacant' || (!assigneeName(r) && r.status !== 'filled')).length,
    days: roles.reduce((n, r) => n + (r.estimated_days || 0), 0),
  }), [roles]);

  const visible = useMemo(() => {
    if (phaseFilter === 'all') return roles;
    return roles.filter((r) => (Array.isArray(r.phases) ? r.phases : []).includes(phaseFilter));
  }, [roles, phaseFilter]);

  async function proposeFromScope() {
    if (!project?.id || !programme?.id) return;
    setProposing(true);
    try {
      if (!roles.length) {
        const rows = PROPOSAL_TEMPLATES.map((t, i) => ({
          programme_id: programme.id,
          project_id: project.id,
          role_label: t.role_label,
          category: t.category,
          phases: t.phases,
          estimated_days: t.estimated_days,
          status: i < 3 ? (i === 0 ? 'filled' : 'approved') : 'proposed',
          order_index: i,
          created_by: profile?.id || null,
        }));
        const { data, error } = await sb
          .from('project_role_proposals')
          .insert(rows)
          .select('*, assignee:profiles!project_role_proposals_assignee_id_fkey(full_name)');
        if (error) throw error;
        setRoles(data || []);
        setToast({ type: 'success', msg: 'Roles proposed from scope' });
      } else {
        const ids = roles.filter((r) => r.status === 'proposed').map((r) => r.id);
        if (ids.length) {
          await sb.from('project_role_proposals').update({ status: 'approved' }).in('id', ids);
        }
        setRoles((prev) => prev.map((r) => (r.status === 'proposed' ? { ...r, status: 'approved' } : r)));
        setToast({ type: 'success', msg: 'Proposed roles approved' });
      }
    } catch (e) {
      console.error('[RoleProposal] propose failed', e);
      setToast({ type: 'error', msg: e.message || 'Could not propose roles' });
    } finally {
      setProposing(false);
    }
  }

  async function approveRole(id) {
    const { error } = await sb.from('project_role_proposals').update({ status: 'approved' }).eq('id', id);
    if (error) {
      setToast({ type: 'error', msg: error.message });
      return;
    }
    setRoles((prev) => prev.map((r) => (r.id === id ? { ...r, status: 'approved' } : r)));
  }

  async function seedAllocationGrid() {
    if (!orgId || !project?.id) return;
    setSeeding(true);
    try {
      const toSeed = roles.filter((r) => r.status === 'approved' && !r.profile_id);
      if (!toSeed.length) {
        setToast({ type: 'success', msg: 'No approved roles need seeding — open allocation grid' });
        navigate(`/project/${project.id}/management/resource`);
        return;
      }
      for (const role of toSeed) {
        const { data: fr } = await sb
          .from('functional_roles')
          .select('id')
          .eq('organisation_id', orgId)
          .eq('full_name', role.role_label)
          .maybeSingle();

        const { data: prof, error: profErr } = await sb
          .from('profiles')
          .insert({
            organisation_id: orgId,
            status: 'vacant',
            display_label: `TBC — ${role.role_label}`,
            functional_role_id: fr?.id || null,
            employment_status: 'active',
          })
          .select('id')
          .single();
        if (profErr) throw profErr;
        await sb.from('project_role_proposals')
          .update({ profile_id: prof.id, status: 'vacant' })
          .eq('id', role.id);
      }
      await load();
      setToast({ type: 'success', msg: `${toSeed.length} role(s) seeded to resource register` });
      navigate(`/project/${project.id}/management/resource`);
    } catch (e) {
      console.error('[RoleProposal] seed failed', e);
      setToast({ type: 'error', msg: e.message || 'Could not seed allocation grid' });
    } finally {
      setSeeding(false);
    }
  }

  if (loading) return <div className="content"><Loader text="Loading role plan…" /></div>;

  return (
    <div className="content">
      {toast && (
        <div className={toast.type === 'success' ? 'toast-success' : 'toast-error'} style={{ marginBottom: 10 }}>
          {toast.msg}
        </div>
      )}

      <div className="chdr" style={{ marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 500, color: '#2c2c2a' }}>Role proposal</div>
          <div style={{ fontSize: 11, color: '#888780', marginTop: 2 }}>
            {project?.name || 'Project'} · Management · Roles & resource plan
          </div>
        </div>
        {canWrite && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button type="button" className="btn-ghost" disabled={proposing} onClick={proposeFromScope}>
              {proposing ? 'Proposing…' : '✦ Propose roles from scope'}
            </button>
            <button
              type="button"
              className="btn-primary"
              style={{ background: '#1D9E75', borderColor: '#1D9E75' }}
              disabled={seeding}
              onClick={seedAllocationGrid}
            >
              {seeding ? 'Seeding…' : 'Seed allocation grid →'}
            </button>
          </div>
        )}
      </div>

      <div className="m4" style={{ marginBottom: 12 }}>
        <div className="met"><div className="mlbl">Roles</div><div className="mval">{summary.total}</div><div className="msub">in plan</div></div>
        <div className="met"><div className="mlbl">Proposed</div><div className="mval" style={{ color: summary.proposed ? '#854F0B' : undefined }}>{summary.proposed}</div><div className="msub">awaiting approval</div></div>
        <div className="met"><div className="mlbl">Vacant</div><div className="mval" style={{ color: summary.vacant ? '#A32D2D' : undefined }}>{summary.vacant}</div><div className="msub">unassigned</div></div>
        <div className="met"><div className="mlbl">Est. effort</div><div className="mval">{summary.days}</div><div className="msub">person-days</div></div>
      </div>

      <div className="card">
        <div className="chdr">
          <span className="ctitle">Roles by phase</span>
          <div className="filter-tabs" style={{ marginBottom: 0 }}>
            <button type="button" className={`ftab${phaseFilter === 'all' ? ' active' : ''}`} onClick={() => setPhaseFilter('all')}>All phases</button>
            {phases.map((p) => (
              <button key={p} type="button" className={`ftab${phaseFilter === p ? ' active' : ''}`} onClick={() => setPhaseFilter(p)}>{p}</button>
            ))}
          </div>
        </div>

        {visible.length === 0 ? (
          <p style={{ padding: '8px 0', fontSize: 11, color: '#888780' }}>
            No roles yet — use &ldquo;Propose roles from scope&rdquo; to generate a starter plan.
          </p>
        ) : (
          <>
            <div className="role-grid-hdr">
              <span>Role</span>
              <span>Category</span>
              <span>Phases</span>
              <span>Days</span>
              <span>Assignee</span>
              <span>Status</span>
              <span />
            </div>
            {visible.map((r) => {
              const st = STATUS_META[r.status] || STATUS_META.proposed;
              const name = assigneeName(r);
              const phaseList = Array.isArray(r.phases) ? r.phases : [];
              return (
                <div key={r.id} className="role-grid-row">
                  <span style={{ fontWeight: 500, color: '#2c2c2a' }}>{r.role_label}</span>
                  <span style={{ color: '#888780', fontSize: 11 }}>{r.category}</span>
                  <span style={{ fontSize: 11, color: '#5a5955' }}>{phaseList.join(', ')}</span>
                  <span>{r.estimated_days}</span>
                  <span style={{ color: name ? '#2c2c2a' : '#b4b2a9' }}>{name || 'TBC'}</span>
                  <Badge variant={st.variant} style={{ fontSize: 10 }}>{st.label}</Badge>
                  {canWrite && r.status === 'proposed' ? (
                    <button type="button" className="cact" style={{ color: '#1D9E75' }} onClick={() => approveRole(r.id)}>Approve</button>
                  ) : <span />}
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}
